import { Controller } from '@hotwired/stimulus';
import Logger from '../../lib/logger';
/**
 * Base MapLeafletController - Core functionality for Leaflet Map Integration

 * Base MapLeafletController - Core functionality for Leaflet Map Integration
 *
 * This base controller provides the core map functionality and serves as the
 * foundation for modular map components. It handles initialization, constants,
 * and basic lifecycle management.
 *
 * @class BaseMapLeafletController
 * @extends {Controller}
 */
class BaseMapLeafletController extends Controller {
    // Instance methods to get configurable values (use Stimulus values with fallbacks)
    get DEFAULT_CENTER() { return this.defaultCenterValue; }
    get DEFAULT_ZOOM() { return this.defaultZoomValue; }
    get MARKER_ZOOM() { return this.markerZoomValue; }
    get CENTER_ZOOM() { return this.centerZoomValue; }
    get CIRCLE_RADIUS() { return this.circleRadiusValue; }
    // Get icon URLs from Stimulus values
    getIcons() {
        return {
            MARKER: this.markerIconValue,
            BIDS: this.bidsIconValue,
            NO_BIDS: this.noBidsIconValue,
            ACTIVE: this.activeIconValue,
            ORDERS: this.ordersIconValue
        };
    }
    // Get tile configuration from Stimulus values
    getTileConfig() {
        return {
            URL: this.tileUrlValue,
            ATTRIBUTION: this.tileAttributionValue
        };
    }
    // Get API endpoints from Stimulus values
    getApiEndpoints() {
        return {
            BIDS_CRM: this.bidsCrmUrlValue,
            QUOTES_CRM: this.quotesCrmUrlValue,
            BID_PROVIDERS: this.bidProvidersUrlValue
        };
    }
    connect() {
        Logger.debug('MapLeafletBaseController connecting', {
            geolocation: "geolocation" in navigator,
            hasMapdivTarget: this.hasMapdivTarget
        });
        if (!this.hasMapdivTarget) {
            Logger.warn('No Mapdiv target found', { element: this.element });
            return;
        }
        try {
            this.initializeController();
            this.initializeTooltips();
            // Note: initMap() and setupDataSources() will be called by the main controller
            // after modules are initialized
            Logger.success('Base map controller initialized successfully');
        }
        catch (error) {
            Logger.error('Failed to initialize map controller', error, {
                controller: this.constructor.name,
                hasMapdivTarget: this.hasMapdivTarget
            });
            this.showErrorMessage('Failed to initialize map. Please refresh the page.');
        }
    }
    // Initialize Bootstrap tooltips
    initializeTooltips() {
        try {
            if (typeof window.initializeTooltips === 'function') {
                window.initializeTooltips();
                Logger.debug('Initialized tooltips via global handler');
            }
            else if (typeof bootstrap !== 'undefined' && bootstrap.Tooltip) {
                // Fallback for isolated contexts or early initialization
                const tooltipTriggerList = this.element.querySelectorAll('[data-bs-toggle="tooltip"]');
                Array.from(tooltipTriggerList).forEach(tooltipTriggerEl => {
                    if (!bootstrap.Tooltip.getInstance(tooltipTriggerEl)) {
                        new bootstrap.Tooltip(tooltipTriggerEl, { container: 'body' });
                    }
                });
                Logger.debug('Initialized tooltips via fallback', { count: tooltipTriggerList.length });
            }
        }
        catch (error) {
            Logger.error('Failed to initialize tooltips', error, {
                controller: this.constructor.name,
                elementCount: this.element.querySelectorAll('[data-bs-toggle="tooltip"]').length
            });
        }
    }
    // Initialize controller state and properties
    initializeController() {
        this.suppressMoveend = false;
        this.spinnerTimeout = null;
        this.spinnerVisible = false;
        this.circle = null;
        this.previousZoom = null;
        this.previousCenter = null;
        this.previousBounds = null;
        // Initialize toggle all state flag
        this.isTogglingAll = false;
        // Initialize debug counters
        this.colorDebugCount = 0;
        this.styleDebugCount = 0;
        // PERFORMANCE: Initialize client-side cache for bid data
        this.bidDataCache = new Map();
        // ABORT: Initialize AbortController for request cancellation
        this.abortController = null;
        // Create debounced methods using value-based delay
        this._debouncedAreaLoad = this.debounce(this.areaLoad.bind(this), this.debounceDelayValue);
    }
    // Setup data sources (KML, TopoJSON, etc.)
    setupDataSources() {
        if (this.hasTopojsonValue && this.topojsonValue) {
            if (this.hasStateValue && this.stateValue) {
                const stateInfoUrl = `/states/ibge_info?id=${this.stateValue}`;
                const stateFileUrl = `/states/get_file?id=${this.stateValue}`;
                this.getLocationData(stateInfoUrl, stateFileUrl);
            }
            else {
                // No state value, ensure spinner is hidden
                this.hideSpinner();
            }
        }
        else if (this.hasKmlValue && this.kmlValue) {
            this.loadkml();
        }
        else {
            // No data sources to load, ensure spinner is hidden
            this.hideSpinner();
        }
    }
    disconnect() {
        try {
            // Clean up event listeners
            if (this.map) {
                if (this.map.hasLayer(this.bids)) {
                    this.map.off('zoomend');
                    this.map.off('moveend');
                    this.map.off('popupopen');
                }
                // Clean up GeoJSON zoom event listener
                this.map.off('zoomend', this.handleGeoZoomEnd);
                // Clean up layers
                this.cleanupLayers();
                // Remove map instance
                this.map.remove();
            }
            // Clear timeouts
            this.clearTimeouts();
            Logger.success('Map controller disconnected successfully');
        }
        catch (error) {
            Logger.error('Error during map controller disconnect', error, {
                controller: this.constructor.name,
                hasMap: !!this.map
            });
        }
    }
    // Clean up all layers and controls
    cleanupLayers() {
        const layersToClean = ['bids', 'geojson', 'mapklayer'];
        const controlsToClean = ['info', 'legend'];
        layersToClean.forEach(layerName => {
            if (this[layerName] && this.map.hasLayer(this[layerName])) {
                this.map.removeLayer(this[layerName]);
            }
        });
        controlsToClean.forEach(controlName => {
            if (this[controlName] && this.map.hasControl && this.map.hasControl(this[controlName])) {
                this.map.removeControl(this[controlName]);
            }
        });
        if (this.circle) {
            this.circle.remove();
            this.circle = null;
        }
    }
    // Clear all timeouts to prevent memory leaks
    clearTimeouts() {
        if (this.spinnerTimeout) {
            clearTimeout(this.spinnerTimeout);
            this.spinnerTimeout = null;
        }
        if (this.abortController) {
            this.abortController.abort();
            this.abortController = null;
        }
    }
    // Get a fresh AbortSignal, canceling any pending ones
    getAbortSignal() {
        if (this.abortController) {
            this.abortController.abort();
        }
        this.abortController = new AbortController();
        return this.abortController.signal;
    }
    // Utility delay function
    delay(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }
    // Debounce utility function
    debounce(fn, delay = 100) {
        let timeout;
        return function (...args) {
            clearTimeout(timeout);
            timeout = setTimeout(() => fn.apply(this, args), delay);
        };
    }
    // Utility method to escape HTML
    escapeHtml(text) {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }
    // Announce message to screen readers
    announceToScreenReader(message, priority = 'polite') {
        try {
            const announcementElement = document.getElementById('map-sr-announcements');
            if (!announcementElement) {
                Logger.warn('Screen reader announcement element not found');
                return;
            }
            // Set aria-live priority
            announcementElement.setAttribute('aria-live', priority);
            // Clear previous announcement
            announcementElement.textContent = '';
            // Add new announcement after a brief delay to ensure it's detected
            setTimeout(() => {
                announcementElement.textContent = message;
                Logger.debug(`Screen reader announcement (${priority})`, { message });
            }, 100);
            // Clear after 5 seconds to avoid clutter
            setTimeout(() => {
                announcementElement.textContent = '';
            }, 5000);
        }
        catch (error) {
            Logger.error('Failed to announce to screen reader', error, { message, priority });
        }
    }
    // Check if bid providers tab is currently active
    isBidProvidersTabActive() {
        return this.hasBidProvidersTabTarget && this.bidProvidersTabTarget.classList.contains('active');
    }
    // Check if bid list tab is currently active
    isBidListTabActive() {
        return this.hasBidsTabTarget && this.bidsTabTarget.classList.contains('active');
    }
    // Dismiss message action (replaces inline onclick handlers)
    dismissMessage(event) {
        event.preventDefault();
        const messageElement = event.currentTarget.closest('.alert');
        if (messageElement) {
            messageElement.remove();
        }
    }
    // Show error message to user (CSP-compliant - uses CSS classes)
    showErrorMessage(message) {
        // Remove any existing error messages
        const existingErrors = this.mapdivTarget.querySelectorAll('.map-error-message');
        existingErrors.forEach(error => error.remove());
        // Create error notification using CSS classes (CSP-compliant)
        const errorDiv = document.createElement('div');
        const classes = ['alert', 'alert-danger', 'map-error-message'];
        if (this.hasErrorClass) {
            classes.push(this.errorClass);
        }
        errorDiv.className = classes.join(' ');
        errorDiv.innerHTML = `
      <button type="button"
              class="btn-close float-end map-message-close"
              data-action="click->map-leaflet#dismissMessage"></button>
      <div class="map-message-content">
        <i class="fas fa-exclamation-triangle"></i>
        <strong>Error:</strong> ${message}
      </div>
    `;
        this.mapdivTarget.appendChild(errorDiv);
        // Auto-remove after 8 seconds
        setTimeout(() => {
            if (errorDiv.parentElement) {
                errorDiv.remove();
            }
        }, 8000);
    }
    // Show informational message (non-error) - CSP-compliant
    showInfoMessage(message, type = 'info') {
        // Remove any existing info messages first
        const existingInfos = this.mapdivTarget.querySelectorAll('.map-info-message');
        existingInfos.forEach(info => info.remove());
        const iconClass = type === 'info' ? 'fa-info-circle' : 'fa-exclamation-triangle';
        const typeClass = type === 'info' ? 'map-info-message--info' : 'map-info-message--warning';
        // Create notification using CSS classes (CSP-compliant)
        const infoDiv = document.createElement('div');
        infoDiv.className = `alert map-info-message ${typeClass}`;
        infoDiv.innerHTML = `
      <button type="button"
              class="btn-close float-end map-message-close"
              data-action="click->map-leaflet#dismissMessage"></button>
      <div class="map-message-content">
        <i class="fas ${iconClass}"></i>
        <strong>Notice:</strong> ${message}
      </div>
    `;
        this.mapdivTarget.appendChild(infoDiv);
        // Auto-remove after 10 seconds for info messages
        setTimeout(() => {
            if (infoDiv.parentElement) {
                infoDiv.classList.add('fade-out');
                setTimeout(() => {
                    if (infoDiv.parentElement) {
                        infoDiv.remove();
                    }
                }, 300);
            }
        }, 10000);
    }
    // ==========================================================================
    // PUBLIC API - Stable interface for external controller interaction
    // ==========================================================================
    /**
     * Get the Leaflet map instance.
     * Use this instead of directly accessing `mapController.map`
     * @returns {L.Map|null} The Leaflet map instance or null if not initialized
     */
    getMap() {
        return this.map || null;
    }
    /**
     * Get the current map bounds.
     * @returns {L.LatLngBounds|null} The current bounds or null if map not ready
     */
    getBounds() {
        if (!this.map)
            return null;
        try {
            return this.map.getBounds();
        }
        catch (error) {
            Logger.warn('Failed to get map bounds', { error, controller: this.constructor.name, hasMap: !!this.map });
            return null;
        }
    }
    /**
     * Get the current map bounds as a URL-friendly string.
     * Format: "south,west,north,east"
     * @returns {string|null} Bounds string or null if not available
     */
    getBoundsString() {
        const bounds = this.getBounds();
        if (!bounds)
            return null;
        try {
            const sw = bounds.getSouthWest();
            const ne = bounds.getNorthEast();
            return `${sw.lat},${sw.lng},${ne.lat},${ne.lng}`;
        }
        catch (error) {
            Logger.warn('Failed to get bounds string', { error, controller: this.constructor.name });
            return null;
        }
    }
    /**
     * Get the bids layer (marker layer group).
     * @returns {L.LayerGroup|null} The bids layer or null
     */
    getBidsLayer() {
        return this.bids || null;
    }
    /**
     * Check if the bids layer is currently visible on the map.
     * @returns {boolean} True if bids layer is visible
     */
    isBidsLayerVisible() {
        if (!this.map || !this.bids)
            return false;
        return this.map.hasLayer(this.bids);
    }
    /**
     * Find a marker by address ID.
     * Searches through all markers in the bids layer for matching address ID.
     * @param {number|string} addressId - The address ID to find
     * @returns {L.Marker|null} The marker or null if not found
     */
    findMarkerByAddressId(addressId) {
        if (!this.bids)
            return null;
        const targetId = String(addressId);
        let foundMarker = null;
        this.bids.eachLayer((layer) => {
            if (foundMarker)
                return; // Already found
            if (layer.options && layer.options.addressId &&
                String(layer.options.addressId) === targetId) {
                foundMarker = layer;
            }
        });
        return foundMarker;
    }
    /**
     * Find a marker by coordinates with optional tolerance.
     * @param {number} lat - Target latitude
     * @param {number} lng - Target longitude
     * @param {number} [tolerance=0.0001] - Coordinate matching tolerance
     * @returns {L.Marker|null} The marker or null if not found
     */
    findMarkerByCoordinates(lat, lng, tolerance = 0.0001) {
        if (!this.bids)
            return null;
        let foundMarker = null;
        let minDistance = tolerance;
        this.bids.eachLayer((layer) => {
            if (foundMarker)
                return; // Already found within tolerance
            const latLng = layer.getLatLng();
            if (!latLng)
                return;
            const latDiff = Math.abs(latLng.lat - lat);
            const lngDiff = Math.abs(latLng.lng - lng);
            if (latDiff < minDistance && lngDiff < minDistance) {
                foundMarker = layer;
                minDistance = Math.max(latDiff, lngDiff);
            }
        });
        return foundMarker;
    }
    /**
     * Get the current provider filter.
     * @returns {Object|null} The current provider filter object or null if none
     */
    getProviderFilter() {
        if (this.providerFilterIdValue && this.providerFilterIdValue > 0) {
            return {
                id: this.providerFilterIdValue,
                name: this.providerFilterNameValue
            };
        }
        return null;
    }
    /**
     * Alias for backwards compatibility internally
     */
    getCurrentProviderFilter() {
        return this.getProviderFilter();
    }
    /**
     * Check if "recent bids only" filter is enabled.
     * @returns {boolean} True if only showing recent bids
     */
    isRecentBidsOnly() {
        return this.showRecentBidsOnlyValue === true;
    }
    /**
     * Check if hover popup is enabled.
     * @returns {boolean} True if popups show on hover
     */
    isHoverPopupEnabled() {
        return this.showPopupOnHoverValue === true;
    }
    /**
     * Get all markers in the bids layer.
     * @returns {L.Marker[]} Array of all markers
     */
    getAllMarkers() {
        if (!this.bids)
            return [];
        const markers = [];
        try {
            this.bids.eachLayer((layer) => {
                if (layer.getLatLng) { // Check if it's a marker-like layer
                    markers.push(layer);
                }
            });
        }
        catch (error) {
            Logger.error('Failed to get all markers', error, {
                controller: this.constructor.name,
                hasBids: !!this.bids
            });
        }
        return markers;
    }
    /**
     * Get the current map center.
     * @returns {{lat: number, lng: number}|null} Center coordinates or null
     */
    getCenter() {
        if (!this.map)
            return null;
        try {
            const center = this.map.getCenter();
            return { lat: center.lat, lng: center.lng };
        }
        catch (error) {
            Logger.warn('Failed to get map center', { error, controller: this.constructor.name, hasMap: !!this.map });
            return null;
        }
    }
    /**
     * Get the current zoom level.
     * @returns {number|null} Zoom level or null
     */
    getZoom() {
        if (!this.map)
            return null;
        try {
            return this.map.getZoom();
        }
        catch (error) {
            Logger.warn('Failed to get zoom level', { error, controller: this.constructor.name, hasMap: !!this.map });
            return null;
        }
    }
    /**
     * Check if map is fully initialized and ready.
     * @returns {boolean} True if map is ready for interaction
     */
    isReady() {
        return !!(this.map && this.bids);
    }
    // ==========================================================================
    // END PUBLIC API
    // ==========================================================================
    // Placeholder methods that will be implemented by modules
    initMap() {
        throw new Error('initMap must be implemented by a module');
    }
    areaLoad(e) {
        throw new Error('areaLoad must be implemented by a module');
    }
    loadkml() {
        throw new Error('loadkml must be implemented by a module');
    }
    getLocationData(stateInfoUrl, stateFileUrl) {
        throw new Error('getLocationData must be implemented by a module');
    }
    handleGeoZoomEnd(e) {
        throw new Error('handleGeoZoomEnd must be implemented by a module');
    }
}
BaseMapLeafletController.targets = [
    'mapdiv',
    'infodiv',
    'spinner',
    'geoButton',
    'bidsButton',
    'recentBidsCheckbox',
    'hoverPopupCheckbox',
    // Tab-related targets (Phase 4)
    'providersContent', // bid-providers-content div
    'providersCountBadge', // bid-count badge
    'bidListContent', // bid-list-content div
    'bidListCountBadge', // bid-list-count badge
    'filterBanner', // map-provider-filter-banner
    'filterProviderName', // filter-provider-name span
    'bidsTab', // bids-tab button
    'bidProvidersTab', // bid-providers-tab button
    'bidProvidersPane', // bid-providers pane
    'bidListPane', // bids_t pane
    // Additional targets (Phase 12)
    'toggleAllKmls', // toggle_all_kmls checkbox
    'kmlCheckbox', // individual kml checkboxes
    'showBidsButton', // showBidsButton button
    'bidsCrmFrame', // bidscrm turbo-frame
    'closeMapButton', // close map button
    // Distance sort targets (Phase 28)
    'distanceSortContainer', // distance-sort-container li
    'distanceSortButton' // distance sort toggle button
];
BaseMapLeafletController.values = {
    kml: String,
    url: String,
    topojson: String,
    state: String,
    lat: Number,
    long: Number,
    ibge: String,
    // Configuration values with defaults
    showRecentBidsOnly: { type: Boolean, default: true },
    showPopupOnHover: { type: Boolean, default: false },
    cacheExpiration: { type: Number, default: 300000 }, // 5 minutes
    maxCacheSize: { type: Number, default: 50 },
    debounceDelay: { type: Number, default: 50 },
    sortByDistance: { type: Boolean, default: false },
    providerFilterId: { type: Number, default: 0 },
    providerFilterName: { type: String, default: "" },
    // Map configuration values (Phase 1 refactoring)
    defaultCenter: { type: Array, default: [-15.7801, -47.9292] }, // Brasília
    defaultZoom: { type: Number, default: 4 },
    markerZoom: { type: Number, default: 10 },
    centerZoom: { type: Number, default: 15 },
    circleRadius: { type: Number, default: 1000 },
    // Tile layer configuration
    tileUrl: { type: String, default: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png' },
    tileAttribution: { type: String, default: '&copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a> contributors' },
    // Icon URLs (configurable, with defaults to local assets)
    markerIcon: { type: String, default: '/assets/map/marker.png' },
    bidsIcon: { type: String, default: '/assets/map/bids.png' },
    noBidsIcon: { type: String, default: '/assets/map/no-bids.png' },
    activeIcon: { type: String, default: '/assets/map/active.png' },
    ordersIcon: { type: String, default: '/assets/map/orders.png' },
    // API endpoints (configurable)
    bidsCrmUrl: { type: String, default: '/bids_crm' },
    quotesCrmUrl: { type: String, default: '/q_crm' },
    bidProvidersUrl: { type: String, default: '/addresses/bid_providers' }
};
BaseMapLeafletController.classes = [
    'error',
    'info',
    'warning',
    'activeButton',
    'inactiveButton',
    'loading'
];
// Static constants for non-configurable values (timing, sizes, anchors)
BaseMapLeafletController.CONSTANTS = {
    // These values are kept static as they don't need per-instance configuration
    DEBOUNCE_DELAY: 50,
    SPINNER_DELAY: 250,
    POPUP_CLOSE_DELAY: 200,
    INFO_MESSAGE_DURATION: 10000,
    MOVEEND_SUPPRESSION_DELAY: 300,
    ICON_SIZE: [48, 48],
    ICON_ANCHOR: [24, 48],
    FLAG_ICON_ANCHOR: [8, 48],
    POPUP_ANCHOR: [0, -20],
    MARKER_POPUP_ANCHOR: [0, -55]
};
// DEPRECATED: Static ICONS preserved for backward compatibility
// Use getIcons() instance method instead
BaseMapLeafletController.ICONS = {
    MARKER: '/assets/map/marker.png',
    BIDS: '/assets/map/bids.png',
    NO_BIDS: '/assets/map/no-bids.png',
    ACTIVE: '/assets/map/active.png',
    ORDERS: '/assets/map/orders.png'
};
// DEPRECATED: Static TILE_CONFIG preserved for backward compatibility
// Use getTileConfig() instance method instead
BaseMapLeafletController.TILE_CONFIG = {
    URL: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    ATTRIBUTION: '&copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a> contributors'
};
export default BaseMapLeafletController;
